import { useState, useCallback } from 'react';
import { XMarkIcon, ArrowUpCircleIcon } from '@heroicons/react/24/outline';
import { useAuthContext } from '../../contexts/AuthContext';
import { billingApi } from '../../services/api';
import type { LimitExceededError, FeatureNotEntitledError } from '../../types/billing';
import { useFlag } from '../../hooks/useFeatureFlags';
// A pure formatter, so the interval refusal reads as a duration rather than as
// a second count. No coupling beyond the function itself.
import { describeInterval } from '../../lib/scheduleCron';

interface UpgradePromptProps {
  /**
   * The 402 limit_exceeded / 403 feature_not_entitled shapes only — the
   * no_active_plan variant renders ChoosePlanPrompt instead (task 465),
   * because its remedy is choosing a plan, not upgrading one.
   */
  error: LimitExceededError | FeatureNotEntitledError;
  onDismiss: () => void;
}

/**
 * Human-readable messages for each limit key.
 */
const LIMIT_MESSAGES: Record<string, { title: string; description: (current: number, cap: number) => string }> = {
  max_endpoints: {
    title: 'Endpoint limit reached',
    description: (current, cap) =>
      `Your plan includes ${cap} endpoint${cap === 1 ? '' : 's'} and you already have ${current}. Upgrade to create more.`,
  },
  included_endpoint_calls: {
    title: 'Call limit reached',
    description: (current, cap) =>
      `You've used ${current} of your ${cap} included endpoint call${cap === 1 ? '' : 's'} this period. Upgrade to keep going.`,
  },
  // Endpoint schedules (task 1003). Two keys, because the plan caps schedules
  // in two different ways: how many may be active, and how often one may fire.
  max_schedules: {
    title: 'Schedule limit reached',
    description: (_current, cap) =>
      `Your plan includes ${cap} active schedule${cap === 1 ? '' : 's'}. Pause or delete one to add another, or upgrade for more.`,
  },
  min_schedule_interval_seconds: {
    title: 'That schedule runs too often',
    description: (current, cap) =>
      `Your plan allows a run at most ${describeInterval(cap)}, and this rule would run ${describeInterval(current)}. Slow the rule down, or upgrade for a shorter interval.`,
  },
};

/**
 * Human-readable messages for each gated feature key (task 1021).
 *
 * The catalog carries a display name for every feature a plan includes, but a
 * refusal names a capability the account does NOT have — so there is no catalog
 * entry to read it from. These entries say the capability in words; anything
 * unmapped falls back to the raw key below, which is a prompt to add it here
 * rather than an acceptable state.
 */
const FEATURE_MESSAGES: Record<string, { title: string; description: string }> = {
  'innovate.schedules': {
    title: 'Schedules are not included in your plan',
    description:
      'Recurring and one-off runs are available on paid plans. Existing schedules are kept but will not fire.',
  },
  api_access: {
    title: 'External access needs a paid plan',
    description:
      'Calling your AI Services from outside Innovate — API keys, anonymous public calls, embeds on other sites and custom domains — needs a paid plan. Building, testing and running them inside the app stays included.',
  },
};

function featureMessage(featureKey: string) {
  const entry = FEATURE_MESSAGES[featureKey];
  if (entry) return entry;
  return {
    title: 'Feature not available on your plan',
    description: `The feature "${featureKey}" is not included in your current plan. Upgrade to unlock it.`,
  };
}

function limitMessage(limitKey: string, current: number, cap: number) {
  const entry = LIMIT_MESSAGES[limitKey];
  if (entry) {
    return { title: entry.title, description: entry.description(current, cap) };
  }
  return {
    title: 'Plan limit reached',
    description: `You have reached the limit for "${limitKey}" on your current plan (${current} / ${cap}). Upgrade for more.`,
  };
}

/**
 * UpgradePrompt is shown when innovate-service returns:
 *   - 402 { error: 'limit_exceeded', limit, current, cap }
 *   - 403 { error: 'feature_not_entitled', feature }
 *
 * It renders an inline card (not a toast) with a clear upgrade CTA.
 * Clicking "Upgrade" creates a Stripe Checkout session via innovate-service
 * (POST /api/billing/session). If the billing endpoint is not yet deployed
 * (404/501), the button shows an "unavailable" message instead of crashing.
 *
 * On return from Stripe Checkout, we force a Keycloak token refresh so the
 * new caps take effect without requiring a re-login.
 */
export default function UpgradePrompt({ error, onDismiss }: UpgradePromptProps) {
  const { refreshToken } = useAuthContext();
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);

  // Billing is an owner concern; the server enforces it with
  // 403 owner_required. The courtesy check now keys off
  // innovate.fe.billing.page, whose role-owner cohort carries the rule
  // (task 692) — no role claim is read here.
  const isNonOwner = !useFlag('innovate.fe.billing.page');

  const { title, description } = error.error === 'limit_exceeded'
    ? limitMessage(error.limit, error.current, error.cap)
    : featureMessage(error.feature);

  const handleUpgrade = useCallback(async () => {
    setUpgrading(true);
    setUpgradeError(null);

    // Build the return URL with a query param so the app knows to refresh
    // the token after Checkout completes.
    const returnUrl = `${window.location.href}${
      window.location.href.includes('?') ? '&' : '?'
    }billing_return=1`;

    try {
      const response = await billingApi.createSession({
        mode: 'checkout',
        returnUrl,
      });
      // Navigate to the Stripe-hosted Checkout page.
      // innovate-service wraps the response: { success, data: { url } }
      window.location.href = response.data.data.url;
    } catch (err: unknown) {
      const e = err as { status?: number; message?: string };
      if (e?.status === 404 || e?.status === 501 || !e?.status) {
        // billing endpoint not yet deployed — degrade gracefully.
        setUpgradeError(
          'Upgrade is not available yet. Please contact support to upgrade your plan.'
        );
      } else {
        setUpgradeError('Something went wrong. Please try again.');
      }
      setUpgrading(false);
    }
  }, []);

  const handleManagePlan = useCallback(async () => {
    setUpgrading(true);
    setUpgradeError(null);

    const returnUrl = `${window.location.href}${
      window.location.href.includes('?') ? '&' : '?'
    }billing_return=1`;

    try {
      const response = await billingApi.createSession({
        mode: 'portal',
        returnUrl,
      });
      // innovate-service wraps the response: { success, data: { url } }
      window.location.href = response.data.data.url;
    } catch (err: unknown) {
      const e = err as { status?: number };
      if (e?.status === 404 || e?.status === 501 || !e?.status) {
        setUpgradeError(
          'Plan management is not available yet. Please contact support.'
        );
      } else {
        setUpgradeError('Something went wrong. Please try again.');
      }
      setUpgrading(false);
    }
  }, []);

  // If the page loaded with ?billing_return=1, force a token refresh so new
  // caps take effect immediately. This is handled by the hook below — we just
  // expose the refresh trigger here so it can be called on mount by the parent.
  void refreshToken; // surfaced for use by useBillingReturn hook

  return (
    <div
      role="alert"
      data-testid="upgrade-prompt"
      className="rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4"
    >
      <div className="flex items-start gap-3">
        <ArrowUpCircleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{title}</p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{description}</p>

          {upgradeError && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{upgradeError}</p>
          )}

          {isNonOwner ? (
            <div className="mt-3">
              <p
                className="text-sm text-gray-700 dark:text-gray-300"
                data-testid="upgrade-prompt-member-note"
              >
                Plan upgrades are managed by your account owner — ask them to upgrade.
              </p>
              <a
                href="/settings/subscription"
                data-testid="view-plan-details-link"
                className="mt-2 inline-flex items-center text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                View plan details
              </a>
            </div>
          ) : (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button
                onClick={handleUpgrade}
                disabled={upgrading}
                data-testid="upgrade-button"
                className="inline-flex items-center px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-medium rounded-md transition-colors"
              >
                {upgrading ? 'Redirecting...' : 'Upgrade plan'}
              </button>
              <button
                onClick={handleManagePlan}
                disabled={upgrading}
                data-testid="manage-plan-button"
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 text-sm font-medium rounded-md transition-colors"
              >
                Manage plan
              </button>
              {/* Plain anchor (not router Link): UpgradePrompt renders in error
                  states and must stay free of router/flag-provider coupling. */}
              <a
                href="/settings/subscription"
                data-testid="view-plan-details-link"
                className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                View plan details
              </a>
            </div>
          )}
        </div>
        <button
          onClick={onDismiss}
          data-testid="upgrade-prompt-dismiss"
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          aria-label="Dismiss"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
